import { Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateSecretDialog } from '@/console/features/vaults/components/create-secret-dialog/create-secret-dialog';
import { TFormSelectSecretProps } from '@/console/features/vaults/components/form-select-secret/form-select-secret.types';
import { SecretSelect } from '@/console/features/vaults/components/secret-select/secret-select';
import { VaultSelect } from '@/console/features/vaults/components/vault-select/vault-select';
import { LockIcon } from '@/shared/assets/icons/lock-icon';
import { LockOpenIcon } from '@/shared/assets/icons/lock-open-icon';
import { TextButton } from '@/shared/components/ui/button/text-button/text-button';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import {
  SecretFragment,
  SecretTypeEnum,
  VaultFragment,
} from '@/shared/types/types.generated';

export const FormSelectSecret = <
  TFieldValues extends FieldValues = FieldValues,
>({
  basePath,
  label,
  acceptedSecretTypes = [SecretTypeEnum.Credential],
  allowCreateSecret = false,
  groupId,
  headerLabel,
}: TFormSelectSecretProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TFieldValues>();
  const _label = label ?? t('vault:add_credentials');
  const _headerLabel = headerLabel ?? t('vault:credentials');

  const getFieldName = (fieldName: string) => {
    if (!basePath) {
      return fieldName as Path<TFieldValues>;
    }

    return `${basePath}.${fieldName}` as Path<TFieldValues>;
  };

  const vaultFieldName = getFieldName('vaultId');
  const secretFieldName = getFieldName('secretId');

  const vaultId = useWatch({
    control,
    name: vaultFieldName,
  });

  const [toggleSecret, setToggleSecret] = useState<boolean>(Boolean(vaultId));
  const hasPermissionToCreateSecret = usePermissions({
    action: 'create',
    entity: ['Secret', 'Vault'],
    attribute: {
      id: vaultId,
      groupId,
    },
  });

  const handleToggleAuth = () => {
    if (toggleSecret) {
      setValue(
        vaultFieldName,
        '' as PathValue<TFieldValues, Path<TFieldValues>>,
      );
      setValue(
        secretFieldName,
        '' as PathValue<TFieldValues, Path<TFieldValues>>,
        { shouldDirty: true },
      );
    }
    setToggleSecret((prev) => !prev);
  };

  const vaultFilter = (vault: VaultFragment) => {
    if (acceptedSecretTypes) {
      return vault.secrets.items.some((secret) =>
        acceptedSecretTypes.includes(secret.secretType),
      );
    }

    return vault.secrets.itemCount > 0;
  };

  const secretFilter = (secret: SecretFragment) => {
    if (acceptedSecretTypes) {
      return acceptedSecretTypes.includes(secret.secretType);
    }

    return true;
  };

  return toggleSecret ? (
    <>
      <Divider />
      <Stack
        direction="row"
        gap="1rem"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h7" color="textSecondary">
          {_headerLabel}
        </Typography>
        <TextButton
          icon={<LockOpenIcon />}
          onClick={handleToggleAuth}
          variant="alertSecondary"
        >
          {t('vault:remove_credentials')}
        </TextButton>
      </Stack>
      <VaultSelect<TFieldValues>
        required={toggleSecret}
        name={vaultFieldName}
        getItemId={(item) => item}
        disableTags={false}
        vaultFilter={vaultFilter}
      />
      {vaultId && (
        <Stack gap="0.5rem">
          <SecretSelect<TFieldValues>
            required={Boolean(vaultId)}
            name={secretFieldName}
            vaultId={vaultId}
            getItemId={(item) => item}
            disableTags={false}
            secretFilter={secretFilter}
          />
          {allowCreateSecret && hasPermissionToCreateSecret && (
            <Stack direction="row" gap="0.5rem" alignItems="center">
              <Typography variant="button2">{t('common:or')}</Typography>
              <CreateSecretDialog
                vaultId={vaultId}
                acceptedSecretTypes={acceptedSecretTypes}
                onCreated={(secret) =>
                  setValue(
                    secretFieldName,
                    secret.secretId as PathValue<
                      TFieldValues,
                      Path<TFieldValues>
                    >,
                    { shouldDirty: true, shouldValidate: true },
                  )
                }
              />
            </Stack>
          )}
        </Stack>
      )}
    </>
  ) : (
    <TextButton icon={<LockIcon />} onClick={handleToggleAuth}>
      {_label}
    </TextButton>
  );
};
